import React, { useState, useEffect } from 'react';
import { Table, Card, Button, message, Tag, Alert } from 'antd';
import { PlusOutlined, PrinterOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { prescriptionService } from '../../services';
import dayjs from 'dayjs';

const Prescriptions = () => {
    const { t } = useTranslation(['prescription', 'common']);
    const navigate = useNavigate();
    const [prescriptions, setPrescriptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchPrescriptions();
    }, []);

    const fetchPrescriptions = async () => {
        setLoading(true);
        try {
            const response = await prescriptionService.getAll({});
            if (response.success) {
                setPrescriptions(response.data);
            }
        } catch (error) {
            message.error(t('prescription:messages.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePrescription = () => {
        message.info({
            content: 'Please go to a patient\'s page to create prescriptions',
            duration: 3
        });
        navigate('/patients');
    };

    const columns = [
        {
            title: t('prescription:number'),
            dataIndex: 'prescriptionNumber',
            key: 'prescriptionNumber'
        },
        {
            title: t('prescription:patient'),
            dataIndex: ['patientId', 'name'],
            key: 'patient',
            render: (name, record) => (
                <Button
                    type="link"
                    onClick={() => navigate(`/patients/${record.patientId?._id}`)}
                >
                    {name}
                </Button>
            )
        },
        {
            title: t('prescription:dentist'),
            dataIndex: ['dentistId', 'name'],
            key: 'dentist'
        },
        {
            title: t('prescription:date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('MMM DD, YYYY')
        },
        {
            title: t('prescription:medications'),
            dataIndex: 'medications',
            key: 'medications',
            render: (medications) => <Tag color="green">{medications?.length || 0} {t('prescription:items')}</Tag>
        },
        {
            title: t('common:status'),
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'default'}>
                    {isActive ? t('prescription:active') : t('prescription:inactive')}
                </Tag>
            )
        },
        {
            title: t('common:actions'),
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<PrinterOutlined />}
                    onClick={() => navigate(`/patients/${record.patientId?._id}`)}
                >
                    {t('prescription:print')}
                </Button>
            )
        }
    ];

    return (
        <Card>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <h1>{t('prescription:title')}</h1>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreatePrescription}>
                    {t('prescription:createPrescription')}
                </Button>
            </div>

            <Alert
                message="How to create prescriptions"
                description="To create a new prescription, go to the Patients page, select a patient, and add the prescription from their detail page."
                type="info"
                icon={<InfoCircleOutlined />}
                showIcon
                closable
                style={{ marginBottom: 16 }}
            />

            <Table
                columns={columns}
                dataSource={prescriptions}
                rowKey="_id"
                loading={loading}
                scroll={{ x: 'max-content' }}
            />
        </Card>
    );
};

export default Prescriptions;
